import {useCallback} from "react";

import type {AiGuideFeedbackProvidedV6} from "@khanacademy/event-schemas";
import {publishAnalyticsEventForMinorConversion} from "@repo/analytics-learning-time";
import {GuideStateApi} from "@repo/guide-state";
import {
    FeedbackType,
    type InteractionFeedback,
    type InteractionID,
} from "@repo/guide-types";

import {mergeFeedback} from "./merge-feedback.ts";

interface RateInteractionFn {
    /**
     * Add or update feedback for an interaction.
     *
     * @param interactionId The ID of the interaction to rate.
     * @param feedback The feedback to add or update.
     */
    (interactionId: InteractionID, feedback: InteractionFeedback): void;
}

/**
 * Hook to provide a function for rating interactions.
 */
export const useRateInteraction = (): RateInteractionFn => {
    const rateInteraction = useCallback<RateInteractionFn>(
        (interactionId, feedback) => {
            const sharedFields: Omit<
                AiGuideFeedbackProvidedV6,
                "isFeedbackPositive"
            > = {
                eventType: "AiGuideFeedbackProvided",
                eventSchemaVersion: 6,
                aiGuideThreadId: GuideStateApi.latest().threadId ?? "",
                aiGuideNavigationId: GuideStateApi.latest().navigation.id,
                aiGuideInteractionId: interactionId,
                freeformFeedbackProvided: false,
                usedEmojiIcon: false,
                feedbackEmoji: null,
                feedbackDropDown: "OTHER_ISSUE",
            };
            switch (feedback.type) {
                case FeedbackType.Quick:
                    publishAnalyticsEventForMinorConversion({
                        ...sharedFields,
                        usedEmojiIcon: true,
                        feedbackEmoji: feedback.sentiment,
                        isFeedbackPositive: feedback.positive,
                    });
                    break;
                case FeedbackType.General:
                    publishAnalyticsEventForMinorConversion({
                        ...sharedFields,
                        freeformFeedbackProvided: Boolean(feedback.note),
                        isFeedbackPositive: feedback.positive,
                    });
                    break;

                case FeedbackType.Negative:
                    publishAnalyticsEventForMinorConversion({
                        ...sharedFields,
                        freeformFeedbackProvided: Boolean(
                            feedback.note || feedback.rewrittenResponse,
                        ),
                        isFeedbackPositive: false,
                        feedbackDropDown: feedback.issue,
                    });
                    break;
                case FeedbackType.Positive:
                    publishAnalyticsEventForMinorConversion({
                        ...sharedFields,
                        freeformFeedbackProvided: Boolean(
                            feedback.note || feedback.rewrittenResponse,
                        ),
                        isFeedbackPositive: feedback.positive,
                    });
                    break;
            }

            GuideStateApi.set("ratedInteractions", (ratedInteractions) => {
                // Merge the feedback with any existing feedback for this
                // interaction.
                const updatedFeedback = mergeFeedback(
                    ratedInteractions.get(interactionId),
                    // $FlowFixMe[incompatible-call] Fix this up when switching back to enums
                    feedback,
                );

                // Update the rated interactions map.
                const updatedMap = new Map(ratedInteractions);
                updatedMap.set(interactionId, updatedFeedback);
                return updatedMap;
            });
        },
        [],
    );

    return rateInteraction;
};
